import * as THREE from 'three';

/**
* PointCloudRadar.
*/
export default class PointCloudRadar {
  /**
  * Constructor
  */
  constructor() {
    this.needsScan = false;

    this.scannedLine = undefined;
    this.targets = undefined;

    this.progress = 0;
    this.nearlyDone = false;

    this.scanBoxMaterial = new THREE.MeshBasicMaterial({
      color: 0xffff0000,
      side: THREE.DoubleSide,
      transparent: true,
      opacity: 0,
      visible: false,
    });
    this.scanBoxGeo = new THREE.BoxGeometry(500, 10, 0.6);

    this.createScanBox();

    this.onCompleted = this.onCompleted.bind(this);
  }

  /**
  * Start
  */
  start() {
    this.scanOrder = [];
    this.needsScan = true;
    this.progress = 0;
    this.scannedLinePositions = null;
    if (this.scanBoxPivot) {
      this.scanBoxPivot.rotation.y = 0;
    }
    this.doRadarScan();
    this.scannedLinePositions = this.createRadarScanLines();
    this.onCompleted();

    this.clear();
  }

  /**
  * Clear
  */
  clear() {
    this.targets.length = 0;
    this.scanOrder = null;

    if (this.withinMesh) {
      this.withinMesh.material.dispose();
      this.withinMesh.geometry.dispose();
      this.withinMesh = null;
    }
  }

  /**
  * Override
  */
  onCompleted() { // eslint-disable-line class-methods-use-this
  }

  /**
  * CreateScanBox.
  */
  createScanBox() {
    this.scanBox = new THREE.Mesh(this.scanBoxGeo, this.scanBoxMaterial);
    this.scanBox.name = 'scanBox';
    this.scanBox.position.x = -255;
    this.scanBoxPivot = new THREE.Object3D();
    this.scanBoxPivot.name = 'scanBoxPivot';
    this.scanBoxPivot.add(this.scanBox);
  }

  /**
  * Do the radar scan.
  */
  doRadarScan() {
    // Do a radar style scan on each ball
    const addedOnRotations = [];

    for (let d = 0; d < 360; d += 1) {
      this.scanBoxPivot.rotation.y -= THREE.Math.degToRad(1);
      const curRot = Math.round(THREE.Math.radToDeg(
          this.scanBoxPivot.rotation.y,
      ));

      this.scanBox.geometry.computeBoundingBox();
      this.scanBoxPivot.updateMatrixWorld(true);

      let prevPoint = new THREE.Vector3(0, 0, 0);
      const zeroPoint = new THREE.Vector3(0, 0, 0);

      for (let i = 0; i < this.targets.length; i += 1) {
        const point = this.targets[i];
        const notAdded = this.scanOrder.indexOf(point) < 0;
        const pointLocal = this.scanBox.worldToLocal(point.clone());
        const isInside = this.scanBox.geometry.boundingBox.containsPoint(
            pointLocal,
        );
        const prevDist = prevPoint.distanceTo(point);

        if (notAdded && isInside) {
          if (addedOnRotations.indexOf(curRot) < 0) {
            if (prevDist > 1) {
              this.scanOrder.push(point);
              addedOnRotations.push(curRot);
              addedOnRotations.push(curRot + 1);
              prevPoint = point;
            }
          } else {
            const distToCenter = point.distanceTo(zeroPoint);
            const prevDistToCenter = prevPoint.distanceTo(zeroPoint);

            if (distToCenter > prevDistToCenter) {
              this.scanOrder[this.scanOrder.length - 1] = point;
              prevPoint = point;
            }
          }
        }
      }
    }
  }

  /**
   * Create Radar Scan Lines.
   * @return {array} List of curve points?
   */
  createRadarScanLines() {
    if (this.scanOrder.length < 4) {
      return false;
    }

    const usedPointsCount = this.scanOrder.length * 0.25;

    // "Averaging"
    const curve = new THREE.CatmullRomCurve3(
        this.scanOrder,
        true,
        'catmullrom',
        0.95,
    );

    let curvePoints = curve.getPoints(usedPointsCount);

    // "Twice"
    const curve2 = new THREE.CatmullRomCurve3(
        curvePoints,
        true,
        'catmullrom',
        0.5,
    );

    curvePoints = curve2.getPoints(Math.round(curvePoints.length * 0.33));

    // "Thrice"
    const curve3 = new THREE.CatmullRomCurve3(
        curvePoints,
        true,
        'catmullrom',
        0.5,
    );

    curvePoints = curve3.getPoints(
        Math.max(16, Math.round(curvePoints.length * 0.5)),
    );

    if (curvePoints.length < 4) {
      return false;
    }

    /*
    Curve to typed for waterLine positions attribute
    Also withinShape checker
    */
    const curveToTyped = new Float32Array(curvePoints.length * 3);
    this.withinShape = new THREE.Shape();
    let n = 0;
    this.withinShape.moveTo(curvePoints[0].x, curvePoints[0].z);

    for (let i = 0; i < curvePoints.length; i += 1) {
      curveToTyped[n] = curvePoints[i].x;
      curveToTyped[n + 1] = curvePoints[i].y;
      curveToTyped[n + 2] = curvePoints[i].z;
      n += 3;

      if (i > 0) this.withinShape.lineTo(curvePoints[i].x, curvePoints[i].z);
    }

    // Final check if center is not within the waterline boundaries
    const extrudeSettings = { steps: 1, depth: 1, bevelEnabled: false };
    const withinMeshGeo = new THREE.ExtrudeGeometry(
        this.withinShape,
        extrudeSettings,
    );

    const withinMeshMat = new THREE.MeshBasicMaterial({
      color: 0x00ff00,
      side: THREE.DoubleSide,
    });

    this.withinMesh = new THREE.Mesh(withinMeshGeo, withinMeshMat);
    this.withinMesh.rotation.x = Math.PI / 2;

    const withinRay = new THREE.Raycaster();
    const withinRayDir = new THREE.Vector3(0, 1, 0);
    withinRay.set(new THREE.Vector3(0, 0, 0), withinRayDir);
    const intersects = withinRay.intersectObject(this.withinMesh);

    if (intersects.length && withinRayDir.dot(intersects[0].face.normal) > 0) {
      return curveToTyped;
    }

    // enabling it anyway:
    return curveToTyped;
  }
}
